import React, { useMemo } from 'react';

/**
 * LineArea — a crisp, dependency-free SVG line + area chart tuned for the
 * sci-fi admin cockpit.
 *
 * Props:
 *  - data: number[]                 (required)
 *  - labels?: string[]              (optional, same length as data)
 *  - width?: number                 (default 560)
 *  - height?: number                (default 160)
 *  - color?: string                 (stroke + glow)
 *  - fillColor?: string             (area fill tint, defaults to color)
 *  - valueFormatter?: (n) => string (default: n.toLocaleString())
 *  - showAxes?: boolean             (default true)
 *  - testid?: string
 */
export const LineArea = ({
  data = [],
  labels = [],
  width = 560,
  height = 160,
  color = '#39FF14',
  fillColor,
  valueFormatter = (n) => Number(n || 0).toLocaleString(),
  showAxes = true,
  testid,
}) => {
  const padL = 36;
  const padR = 12;
  const padT = 10;
  const padB = showAxes ? 22 : 10;

  const { pts, pathLine, pathArea, maxV, minV, lastY, lastX } = useMemo(() => {
    if (!data.length) {
      return { pts: [], pathLine: '', pathArea: '', maxV: 0, minV: 0, lastY: 0, lastX: 0 };
    }
    const maxV = Math.max(...data, 1);
    const minV = 0;
    const chartW = width - padL - padR;
    const chartH = height - padT - padB;
    const stepX = data.length > 1 ? chartW / (data.length - 1) : chartW;
    const pts = data.map((v, i) => {
      const x = padL + i * stepX;
      const y = padT + chartH - ((v - minV) / (maxV - minV || 1)) * chartH;
      return [x, y];
    });
    const pathLine = pts
      .map(([x, y], i) => (i === 0 ? `M${x.toFixed(2)},${y.toFixed(2)}` : ` L${x.toFixed(2)},${y.toFixed(2)}`))
      .join('');
    const pathArea = `${pathLine} L ${pts[pts.length - 1][0].toFixed(2)},${(height - padB).toFixed(2)} L ${pts[0][0].toFixed(2)},${(height - padB).toFixed(2)} Z`;
    return {
      pts,
      pathLine,
      pathArea,
      maxV,
      minV,
      lastX: pts[pts.length - 1][0],
      lastY: pts[pts.length - 1][1],
    };
  }, [data, width, height, padL, padR, padT, padB]);

  if (!data.length) {
    return <svg width={width} height={height} aria-hidden data-testid={testid} />;
  }

  const gradId = `linearea-${Math.random().toString(36).slice(2, 8)}`;
  const glowId = `glow-${Math.random().toString(36).slice(2, 8)}`;
  const fill = fillColor || color;

  // ----- Y axis gridlines + ticks (4) -----
  const yTicks = showAxes
    ? [0, 0.33, 0.66, 1].map((p) => ({
        y: padT + (height - padT - padB) * (1 - p),
        label: valueFormatter(Math.round(minV + (maxV - minV) * p)),
      }))
    : [];

  // ----- X axis ticks (first, middle, last) -----
  const xTicks = showAxes && labels.length
    ? [0, Math.floor(labels.length / 2), labels.length - 1].map((i) => ({
        x: pts[i]?.[0] ?? padL,
        label: labels[i]?.slice(5) ?? '', // show MM-DD only
      }))
    : [];

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} data-testid={testid} aria-hidden>
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={fill} stopOpacity="0.42" />
          <stop offset="100%" stopColor={fill} stopOpacity="0" />
        </linearGradient>
        <filter id={glowId} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="2.4" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* grid */}
      {showAxes &&
        yTicks.map((t, i) => (
          <g key={`g-${i}`}>
            <line
              x1={padL}
              x2={width - padR}
              y1={t.y}
              y2={t.y}
              stroke="rgba(255,255,255,0.055)"
              strokeDasharray="2 4"
            />
            <text x={6} y={t.y + 3} fontSize="9" fill="rgba(255,255,255,0.42)" fontFamily="var(--font-mono)">
              {t.label}
            </text>
          </g>
        ))}

      {/* area */}
      <path d={pathArea} fill={`url(#${gradId})`} />

      {/* line */}
      <path d={pathLine} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" filter={`url(#${glowId})`} />

      {/* dots at every point (subtle) */}
      {pts.map(([x, y], i) => (
        <circle key={`p-${i}`} cx={x} cy={y} r={i === pts.length - 1 ? 3.2 : 1.4} fill={color} opacity={i === pts.length - 1 ? 1 : 0.5} />
      ))}

      {/* last-point glow halo */}
      <circle cx={lastX} cy={lastY} r="7" fill={color} opacity="0.14" />

      {/* x axis labels */}
      {showAxes &&
        xTicks.map((t, i) => (
          <text key={`x-${i}`} x={t.x} y={height - 6} fontSize="9" fill="rgba(255,255,255,0.5)" fontFamily="var(--font-mono)" textAnchor={i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'}>
            {t.label}
          </text>
        ))}
    </svg>
  );
};

export default LineArea;
